/* search.js — recherche prédictive « Tiitoune Aurum » (chargé par le header).
   Écoute la saisie dans l'input du formulaire [data-predictive-search-form]
   (debounce 300 ms, minimum 2 caractères), interroge /search/suggest avec
   section_id=predictive-search (Section Rendering API), puis injecte le
   markup rendu (snippets/predictive-search.liquid) dans le conteneur
   [data-predictive-results] du header.

   Périmètre volontairement étroit : la liste de résultats + le clavier
   (flèches via aria-activedescendant, motif combobox). L'ouverture et la
   fermeture du panneau de recherche (dont Échap) restent gérées par
   header.js — aucun double-binding.
   Vanilla JS, délégation sur document, garde anti double-exécution. */
(function () {
  'use strict';

  /* Garde anti double-exécution (éditeur de thème, scripts dupliqués) */
  if (window.__aurumPredictive) return;
  window.__aurumPredictive = true;

  /* Racine des routes Shopify (préfixe de langue éventuel) */
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  var DEBOUNCE = 300;  /* ms avant la requête */
  var MIN_CHARS = 2;   /* longueur minimale de la requête */

  var timer = null;
  var controller = null; /* AbortController de la requête en vol */
  var activeIndex = -1;  /* option active du listbox (-1 : aucune) */
  var lastQuery = '';    /* dernière saisie : écarte les réponses périmées */

  var INPUT_SELECTOR = '[data-predictive-search-form] input[name="q"]';

  function noop() {}

  function getForm(input) {
    return input.closest('[data-predictive-search-form]');
  }

  function getResultsBox(form) {
    return form.querySelector('[data-predictive-results]');
  }

  function getOptions(form) {
    return form.querySelectorAll('[data-predictive-results] [role="option"]');
  }

  /* Zone aria-live polite persistante (créée une fois, jamais remplacée :
     une zone live re-créée à chaque swap ne serait pas annoncée). */
  function ensureLiveRegion(form) {
    var live = form.querySelector('[data-predictive-live]');
    if (!live) {
      live = document.createElement('div');
      live.className = 'visually-hidden';
      live.setAttribute('data-predictive-live', '');
      live.setAttribute('aria-live', 'polite');
      form.appendChild(live);
    }
    return live;
  }

  /* Sémantique combobox posée en JS (l'input du header reste un simple
     champ de recherche sans JS). */
  function setupCombobox(input) {
    if (input.dataset.aurumCombobox === '1') return;
    input.dataset.aurumCombobox = '1';
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-haspopup', 'listbox');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', 'PredictiveResultsList');
  }

  function clearResults(input) {
    var form = getForm(input);
    if (!form) return;
    var box = getResultsBox(form);
    if (box) box.innerHTML = '';
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    activeIndex = -1;
  }

  /* Option active : classe .is-active + aria-selected + aria-activedescendant */
  function setActive(input, form, index) {
    var options = getOptions(form);
    if (options.length === 0) return;
    Array.prototype.forEach.call(options, function (option, i) {
      var isActive = i === index;
      option.classList.toggle('is-active', isActive);
      option.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    activeIndex = index;
    if (index >= 0) {
      input.setAttribute('aria-activedescendant', options[index].id);
      if (options[index].scrollIntoView) {
        options[index].scrollIntoView({ block: 'nearest' });
      }
    } else {
      input.removeAttribute('aria-activedescendant');
    }
  }

  function fetchResults(input, query) {
    if (controller) controller.abort();
    controller = ('AbortController' in window) ? new AbortController() : null;

    var params = new URLSearchParams();
    params.set('q', query);
    params.set('resources[type]', 'product');
    params.set('resources[limit]', '6');
    params.set('section_id', 'predictive-search');

    fetch(ROOT + 'search/suggest?' + params.toString(), controller ? { signal: controller.signal } : undefined)
      .then(function (response) { return response.text(); })
      .then(function (html) {
        if (query !== lastQuery) return; /* réponse périmée : on ignore */
        var form = getForm(input);
        if (!form) return;
        var box = getResultsBox(form);
        if (!box) return;

        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-predictive-search-results]');
        if (!fresh) return;

        box.innerHTML = fresh.innerHTML;
        activeIndex = -1;
        input.removeAttribute('aria-activedescendant');
        input.setAttribute('aria-expanded', getOptions(form).length > 0 ? 'true' : 'false');

        /* Annonce du nombre de suggestions (texte localisé rendu côté Liquid) */
        var countEl = box.querySelector('[data-predictive-count]');
        ensureLiveRegion(form).textContent = countEl ? countEl.textContent.trim() : '';
      })
      .catch(noop); /* abort / réseau : silencieux */
  }

  /* ------------------------------------------------------------------
     Délégation d'événements — l'input peut être re-rendu par l'éditeur
     ------------------------------------------------------------------ */

  /* Saisie : debounce 300 ms, minimum 2 caractères */
  document.addEventListener('input', function (event) {
    /* Composition IME (CJK, autocorrect mobile, touches mortes) : on attend la fin
       de composition pour ne pas mitrailler l'API avec des états intermédiaires. */
    if (event.isComposing || event.keyCode === 229) return;
    var input = event.target;
    if (!input || !input.matches || !input.matches(INPUT_SELECTOR)) return;
    setupCombobox(input);

    var query = input.value.trim();
    lastQuery = query;
    window.clearTimeout(timer);

    if (query.length < MIN_CHARS) {
      if (controller) controller.abort();
      clearResults(input);
      return;
    }
    timer = window.setTimeout(function () {
      fetchResults(input, query);
    }, DEBOUNCE);
  });

  /* Clavier : flèches haut/bas dans le listbox, Entrée ouvre l'option active.
     Échap : header.js ferme le panneau ; on nettoie seulement le listbox. */
  document.addEventListener('keydown', function (event) {
    var input = event.target;
    if (!input || !input.matches || !input.matches(INPUT_SELECTOR)) return;
    var form = getForm(input);
    if (!form) return;
    var options = getOptions(form);

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (options.length === 0) return;
      event.preventDefault();
      var next = event.key === 'ArrowDown' ? activeIndex + 1 : activeIndex - 1;
      if (next >= options.length) next = 0;
      if (next < 0) next = options.length - 1;
      setActive(input, form, next);
    } else if (event.key === 'Enter') {
      if (activeIndex < 0 || options.length === 0) return; /* soumission native */
      var link = options[activeIndex].querySelector('a[href]');
      if (link) {
        event.preventDefault();
        window.location.href = link.href;
      }
    } else if (event.key === 'Escape') {
      clearResults(input);
    }
  });
})();
